import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import { getIndustryTopicLanguageName } from "util/locale";
import { topicLibraryPath } from "util/topicLibraryPaths";
import styles from "./industrySector.module.scss";

const ICON_MAP = {
  "shield-halved": "fa-solid fa-shield-halved",
  "building-columns": "fa-solid fa-building-columns",
  hotel: "fa-solid fa-hotel",
  "bag-shopping": "fa-solid fa-bag-shopping",
  "tower-broadcast": "fa-solid fa-tower-broadcast",
  coins: "fa-solid fa-coins",
  "cart-shopping": "fa-solid fa-cart-shopping",
  "heart-pulse": "fa-solid fa-heart-pulse",
  "graduation-cap": "fa-solid fa-graduation-cap",
  car: "fa-solid fa-car",
  bolt: "fa-solid fa-bolt",
  film: "fa-solid fa-film",
  plane: "fa-solid fa-plane",
  utensils: "fa-solid fa-utensils",
  microchip: "fa-solid fa-microchip",
  industry: "fa-solid fa-industry",
};

const getIndustryTopicsHref = (industryId) => {
  const params = new URLSearchParams({ industry: String(industryId), tab: "industry" });
  return topicLibraryPath({ search: params });
};

const IndustrySectorView = ({ onSelectIndustry, language }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [defaultIndustry, setDefaultIndustry] = useState(null);
  const [otherIndustries, setOtherIndustries] = useState([]);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const fetchSummary = async () => {
    setLoading(true);
    const response = await apiRequest(
      "library/industry_topics/summary",
      "POST",
      { language: language || getIndustryTopicLanguageName(i18n.language) },
      true
    );
    if (response?.success) {
      setDefaultIndustry(response.success.default_industry);
      setOtherIndustries(response.success.other_industries || []);
    }
    setLoading(false);
  };

  const getIconClass = (icon) => ICON_MAP[icon] || ICON_MAP["industry"];

  const typeLabel = (type) => {
    if (type === "genai") return t("topic_library.genai_badge");
    if (type === "multilabel")
      return t("topic_library.type_multilabel", { defaultValue: "Multilabel" });
    if (type === "rule_based")
      return t("topic_library.type_rule_based", { defaultValue: "Rule-based" });
    return type;
  };

  const renderTypeBadges = (industry) => {
    const type =
      industry?.primary_type ||
      (industry?.is_genai ? "genai" : null) ||
      (industry?.types?.[0] ?? null);
    if (!type) return null;
    return (
      <div className={styles.typeBadges}>
        <span
          className={`${styles.typeBadge} ${styles[`typeBadge_${type}`] || ""}`}
          title={
            type === "genai"
              ? t("topic_library.genai_tooltip")
              : type === "multilabel"
              ? t("topic_library.type_multilabel_tooltip", {
                  defaultValue:
                    "Industry topics classified by a supervised multilabel model.",
                })
              : t("topic_library.type_rule_based_tooltip", {
                  defaultValue:
                    "Industry topics matched with boolean / rule-based queries.",
                })
          }
        >
          {typeLabel(type)}
        </span>
      </div>
    );
  };

  const handleIndustryLinkClick = (e, industryId) => {
    e.preventDefault();
    onSelectIndustry(industryId);
  };

  const renderTopicChips = (topics) => {
    return (
      <div className={styles.topicChips}>
        {topics.map((topic) => (
          <span key={topic.id} className={styles.chip}>
            {topic.name}
            {topic.child_count > 0 && (
              <span className={styles.chipCount}> ({topic.child_count})</span>
            )}
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={styles.loadingWrapper}>
        <Spin />
      </div>
    );
  }

  return (
    <div className={styles.sectorView}>
      {defaultIndustry && (
        <>
          <p className={styles.sectionLabel}>
            {t("topic_library.sector.your_default_industry")}
          </p>
          <div className={`${styles.industryCard} ${styles.defaultCard}`}>
            <span className={styles.defaultBadge}>
              {t("topic_library.sector.default")}
            </span>
            <div className={styles.defaultCardInner}>
              <div className={styles.iconWrapper}>
                <i className={getIconClass(defaultIndustry.icon)} />
              </div>
              <div className={styles.cardInfo}>
                <h3 className={styles.industryName}>{defaultIndustry.name}</h3>
                {renderTypeBadges(defaultIndustry)}
                <p className={styles.topicCount}>
                  {defaultIndustry.total}{" "}
                  {t("topic_library.sector.topics_available")}
                </p>
                {defaultIndustry.topics.length > 0 &&
                  renderTopicChips(defaultIndustry.topics)}
                <a
                  className={styles.viewBtn}
                  href={getIndustryTopicsHref(defaultIndustry.id)}
                  onClick={(e) => handleIndustryLinkClick(e, defaultIndustry.id)}
                >
                  {t("topic_library.sector.view_topics")}
                </a>
              </div>
            </div>
          </div>
        </>
      )}

      {otherIndustries.length > 0 && (
        <>
          <p className={styles.sectionLabel} style={{ marginTop: 28 }}>
            {t("topic_library.sector.other_sectors")}
          </p>
          <div className={styles.otherGrid}>
            {otherIndustries.map((ind) => (
              <div key={ind.id} className={styles.industryCard}>
                <div className={styles.smallCardInner}>
                  <div className={`${styles.iconWrapper} ${styles.iconWrapperSmall}`}>
                    <i className={getIconClass(ind.icon)} />
                  </div>
                  <div className={styles.cardInfo}>
                    <h3 className={styles.industryName}>{ind.name}</h3>
                    {renderTypeBadges(ind)}
                    <p className={styles.topicCount}>
                      {ind.total} {t("topic_library.sector.topics_available")}
                    </p>
                  </div>
                </div>
                <a
                  className={styles.exploreBtn}
                  href={getIndustryTopicsHref(ind.id)}
                  onClick={(e) => handleIndustryLinkClick(e, ind.id)}
                >
                  {t("topic_library.sector.explore")}
                </a>
              </div>
            ))}
          </div>
        </>
      )}

      {!defaultIndustry && otherIndustries.length === 0 && (
        <div className={styles.emptyState}>
          <i className="fa-solid fa-industry" />
          <p>{t("topic_library.sector.no_industries")}</p>
        </div>
      )}
    </div>
  );
};

export default IndustrySectorView;
